// Payments (WI12 decisions 13, 18 to 20, 22, 24): whether payments and invoices are on, and every
// order, subscription and payment of the tenant. Orders and subscriptions are made from a member's page.
import { api, openDocument, send } from "./api";
import { day, money, toMinor, when } from "./format";
import { useAct } from "./Members";
import { usePoll } from "./Pages";
import type { Billing } from "./types";

export function Payments({ admin, onMember }: { admin: boolean; onMember: (household: string) => void }) {
  const { data, error, reload } = usePoll(() => api<Billing>("/api/billing"), 30000);
  const { act, problem } = useAct(reload);
  if (error) {
    return <p className="note warn">{error}</p>;
  }
  if (!data) {
    return <p className="muted">Loading…</p>;
  }
  return (
    <div className="stack wide">
      <p className="note">
        Payments are {data.enabled ? <strong>on</strong> : <strong>off</strong>}
        {data.enabled &&
          (data.provider
            ? ` through ${data.provider === "fake" ? "a stand-in that moves no money (dev)" : data.provider}`
            : data.refused
              ? ", but the saved xPay keys are refused here: dev takes only sandbox keys, production only live ones"
              : ", but xPay's keys aren't saved yet")}
        {data.enabled && !data.pass_through && ". Pass-through payments are off until Akshat says how they should work"}
        . GST invoices are{" "}
        {data.invoices.ready ? (
          <strong>on</strong>
        ) : (
          <>
            <strong>off</strong>
            {data.invoices.missing.length > 0 && ` until the accountant's rules are in the settings (${data.invoices.missing.join(", ")})`}
          </>
        )}
        .
      </p>
      {problem && <p className="note warn">{problem}</p>}

      <h2>Orders</h2>
      {data.orders.length === 0 ? (
        <p className="muted">None yet.</p>
      ) : (
        <table className="list">
          <thead>
            <tr>
              <th>Member</th>
              <th>For</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Made</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.id} onClick={() => onMember(o.household_id)}>
                <td>{o.household_name}</td>
                <td>
                  {o.description} <span className="muted">({o.kind.replace("_", "-")})</span>
                </td>
                <td>{money(o.amount_minor, o.currency)}</td>
                <td>{o.status}</td>
                <td>{day(o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Subscriptions</h2>
      {data.subscriptions.length === 0 ? (
        <p className="muted">None yet.</p>
      ) : (
        <table className="list">
          <thead>
            <tr>
              <th>Member</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Paid until</th>
            </tr>
          </thead>
          <tbody>
            {data.subscriptions.map((s) => (
              <tr key={s.id} onClick={() => onMember(s.household_id)}>
                <td>{s.household_name}</td>
                <td>
                  {s.plan_name} <span className="muted">{money(s.amount_minor, s.currency)} a {s.period}</span>
                </td>
                <td>{s.status}</td>
                <td>{day(s.current_end)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Payments received</h2>
      {data.payments.length === 0 ? (
        <p className="muted">None yet.</p>
      ) : (
        <table className="list">
          <thead>
            <tr>
              <th>Member</th>
              <th>Amount</th>
              <th>Status</th>
              <th>When</th>
              <th>Invoice</th>
            </tr>
          </thead>
          <tbody>
            {data.payments.map((p) => (
              <tr key={p.id}>
                <td>
                  <button className="link" onClick={() => onMember(p.household_id)}>
                    {p.household_name}
                  </button>
                </td>
                <td>
                  {money(p.amount_minor, p.currency)}
                  {p.refunded_minor > 0 && <span className="muted"> ({money(p.refunded_minor, p.currency)} refunded)</span>}
                </td>
                <td>
                  {p.status} {p.method && <span className="muted">{p.method}</span>}
                </td>
                <td>{when(p.captured_at ?? p.created_at)}</td>
                <td>
                  {p.invoice_number ? (
                    p.invoice_document_id ? (
                      <button className="link" onClick={() => act(() => openDocument(p.invoice_document_id!, `${p.invoice_number}.pdf`))}>
                        {p.invoice_number}
                      </button>
                    ) : (
                      p.invoice_number
                    )
                  ) : admin && data.invoices.ready && p.status === "captured" ? (
                    <button className="link" onClick={() => act(() => send(`/api/payments/${p.id}/invoice`))}>
                      issue
                    </button>
                  ) : (
                    ""
                  )}
                  {admin && data.provider && ["captured", "partially_refunded"].includes(p.status) && (
                    <button
                      className="link"
                      onClick={() => {
                        const left = (p.amount_minor - p.refunded_minor) / 100;
                        const answer = window.prompt(`Refund how much (${p.currency})? Up to ${left}; empty for all of it.`, "");
                        if (answer === null) {
                          return;
                        }
                        const amount = answer.trim() ? toMinor(answer) : null;
                        act(() => send(`/api/payments/${p.id}/refund`, { amount_minor: amount }));
                      }}
                    >
                      {" "}
                      refund
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
